import type { StateClassKey } from "@tdelivr/design-tokens";

/**
 * What each board puts in its detail panel, away from any rendering.
 *
 * The design draws a detail panel on every board in this console, and `CLAUDE.md` names row
 * detail panels among the things a build may not drop — so a board whose rows were inert would be
 * a board missing something the design has.
 *
 * This file is a copy of the dispatch and finance consoles', deliberately rather than a shared
 * package. They are separate applications with separate dependency graphs, and a package existing
 * only to hold a handful of small functions would couple three release cycles to save forty lines.
 * The calculation was re-run when this third copy was made and came out the same way; ADR 0021 is
 * the standing reason.
 *
 * ## The rule every field here follows
 *
 * **A field appears when the operation behind the board published it, and is absent otherwise.** No
 * row is enriched by a second request: the panel shows what the board already holds, which is why
 * opening a row cannot fail, cannot be slow, and cannot answer 403 on a screen that just drew the
 * row. Where the design's panel names something the service does not publish, the field carries the
 * absence and says which service would have to publish it — the treatment the deliveries board's
 * `Brand` column and the customers board's `Phone` already use.
 *
 * That is also why these builders take the row types the boards already use rather than the
 * services' own: the panel is a second reading of a row on screen, not a second source for it.
 */

/** One line of the panel's field table. */
export interface DetailField {
  readonly name: string;
  readonly value: string;
  /** Geist Mono, for an identifier, a code or a timer — the design's own rule. */
  readonly mono?: boolean;
  /**
   * A field the board's operation does not publish, drawn as the design's `—` in `--text3`.
   *
   * Never a blank and never a guess. `title` carries which service would have to publish it, so the
   * absence is answerable rather than merely visible.
   */
  readonly absent?: boolean;
  readonly title?: string;
}

/** Everything the panel draws for one row. */
export interface RecordDetail {
  /** The row's own name, or its identifier where the service publishes no name. */
  readonly headline: string;
  /** The identifier, always shown and always monospaced, because it is what a report is filed on. */
  readonly identifier: string;
  /** The service codes behind this row, in the mono chip the design puts under the headline. */
  readonly source: string;
  /**
   * The badge at the head of the panel: the same state the row shows, never a second reading.
   *
   * `code` and `status` travel with it because `StateBadge` takes the owning service and the
   * contract's own word, and refuses to draw a badge for a status its table does not cover rather
   * than inventing a grey one. Carrying them here means the panel's badge is the row's badge —
   * computed once, by the board, from the same fields.
   */
  readonly state?: {
    readonly classKey: StateClassKey;
    readonly label: string;
    readonly code: string;
    readonly status: string;
  };
  readonly fields: readonly DetailField[];
  /** The sentence under the fields, where this board has one worth repeating. */
  readonly note?: string;
}

/** A present field. */
export function field(name: string, value: string, mono = false): DetailField {
  return mono ? { name, value, mono: true } : { name, value };
}

/**
 * A field this board cannot fill, with the reason attached.
 *
 * Takes the reason rather than defaulting one, because "not published" is not a reason — the
 * useful sentence names the service and what it would have to publish, which is what turns a blank
 * on a screen into an entry somebody can act on.
 */
export function absent(name: string, why: string): DetailField {
  return { name, value: "—", absent: true, title: why };
}

/**
 * A field that is present only when the value is.
 *
 * `undefined` and the empty string both mean the operation answered without it, and both drop the
 * line rather than drawing an empty one — which is different from `absent`, where the *contract*
 * has no such field at all. A panel that drew both the same way would say this platform cannot
 * publish a value when the truth is that this row does not have one.
 */
export function optional(
  name: string,
  value: string | undefined | null,
  mono = false,
): readonly DetailField[] {
  return value === undefined || value === null || value === "" ? [] : [field(name, value, mono)];
}

/**
 * An instant, as the console writes one.
 *
 * The board's own clock formats the relative ages; this is the absolute one, because a panel is
 * where somebody reads the actual time to put in a message to somebody else.
 */
export function at(instant: string | undefined | null): string | undefined {
  // Both, because a contract publishes `null` for an instant that has not happened and a row the
  // board shaped itself may simply not carry the key. Neither is a time, and both drop the line.
  if (instant === undefined || instant === null) return undefined;
  const parsed = Date.parse(instant);
  if (Number.isNaN(parsed)) return instant;
  return new Date(parsed).toISOString().replace("T", " ").replace(".000Z", " UTC");
}

/**
 * A coordinate pair, at the precision a person can act on.
 *
 * Five decimal places is about a metre, and more digits on a screen read as more certainty than a
 * phone's fix has. The accuracy, where the service publishes one, is a separate field rather than
 * being folded in here.
 */
export function coordinate(latitude: number, longitude: number): string {
  return `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
}

/*
 * No money helper here. `money.ts` already has one, and it is better than the one this file
 * briefly carried: it knows which currencies have no minor units, refuses a non-integer amount
 * rather than rounding it, and renders a negative with a real minus sign. A second implementation
 * of the one rule CLAUDE.md states outright — currency code and full minor units, always — is
 * exactly the kind of duplication that ends with two screens disagreeing about a figure.
 */

/**
 * The action button this console's design draws in the panel's footer, by state class.
 *
 * This is the one piece of the detail panel where the support design and the finance design differ.
 * `20 WP19 Support Console.dc.html` carries a table the finance file does not:
 *
 *     const act={human:['Open approval','approval'],failed:['Retry now','replay'],
 *                caution:['Fix issue','build'],waiting:['Resend','send'],
 *                closed:['Restore','restore'],active:['View progress','monitoring'],
 *                lock:['View versions','history']}[k]
 *
 * — and `actS` sets `display: act ? 'inline-flex' : 'none'`, so `done`, `idle` and `unknown` draw no
 * button at all. Both halves are carried here: the label and the glyph for the seven classes that
 * have one, and nothing for the three that do not.
 *
 * **Every one of the seven is refused, and that is a measurement rather than a shortcut.** Each
 * names a command, and on this console none of them exists:
 *
 *   - `Open approval` — nothing on the platform records an approval of a support case. SUP-01's
 *     transitions are a status change, with no approver and no second person.
 *   - `Retry now` and `Resend` — SUP-01 sends nothing. There is no reply operation anywhere, which
 *     is the absence `Case timeline` is also built around.
 *   - `Fix issue` — not an operation; the design's own word for "go and do the thing", which on a
 *     case means replying, which does not exist.
 *   - `Restore` — SUP-01 can transition a closed case, but only over `/internal/v1`, and nothing
 *     publishes what a reopen would restore it *to*.
 *   - `View progress` — the timeline, which is a page in the rail rather than a command.
 *   - `View versions` — SUP-01 publishes a `version` number and no history of them. FIN-04's
 *     rate-card-style version read does not exist here.
 *
 * So the button is drawn, in the design's words and with the design's glyph, and carries the reason
 * on its own title. A footer the build quietly dropped would be a footer nobody could see was
 * missing; a button that did nothing when pressed would be worse.
 */
export interface DetailAction {
  /** The design's own label for this state class. */
  readonly label: string;
  /** The design's own Material Symbols Rounded glyph name. */
  readonly glyph: string;
  /** Why pressing it would do nothing, for the button's title. Always present: all seven are refused. */
  readonly refusedBecause: string;
}

const DETAIL_ACTIONS: Readonly<Record<string, DetailAction>> = {
  human: {
    label: "Open approval",
    glyph: "approval",
    refusedBecause:
      "Nothing on the platform records an approval of a support case. SUP-01's transitions change a status and name no approver.",
  },
  failed: {
    label: "Retry now",
    glyph: "replay",
    refusedBecause:
      "SUP-01 sends nothing, so there is nothing to retry. No reply operation exists anywhere on the platform.",
  },
  caution: {
    label: "Fix issue",
    glyph: "build",
    refusedBecause:
      "Fixing a case means replying to it, and no reply operation exists anywhere on the platform.",
  },
  waiting: {
    label: "Resend",
    glyph: "send",
    refusedBecause:
      "SUP-01 sends nothing, so there is nothing to resend. No reply operation exists anywhere on the platform.",
  },
  closed: {
    label: "Restore",
    glyph: "restore",
    refusedBecause:
      "SUP-01's transition route is /internal/v1, which the shared front door does not serve, and nothing publishes which status a reopen would restore a case to.",
  },
  active: {
    label: "View progress",
    glyph: "monitoring",
    refusedBecause:
      "Progress on a case is its timeline, which is a page in the rail rather than a command on a row.",
  },
  lock: {
    label: "View versions",
    glyph: "history",
    refusedBecause:
      "SUP-01 publishes a version number and no history of them, so there are no versions to list.",
  },
};

/** The footer action for a state class, or nothing where the design draws none. */
export function detailActionFor(classKey: string): DetailAction | undefined {
  return DETAIL_ACTIONS[classKey];
}
